<script setup>
// 颜色工具（nuxt#19 P3）。一个组件复刻 design 三个单页的 inline <script>：
//   mode="palette"  → Color Palette Generator（随机和谐配色 + 锁定 + Space 重生成 + 复制）
//   mode="gradient" → CSS Gradient Generator（线性/径向 + 角度 + 色标 + 实时预览/复制 CSS，无上传）
//   mode="picker"   → Image Color Picker（上传→canvas 取色 HEX/RGB/HSL + 自动提取 6 色调色板）
// 纯浏览器：Canvas / FileReader / URL.createObjectURL / clipboard，无任何 npm 依赖。
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { fileTooLargeMsg, looksLikeEditableImage } from "@/settings/free-tools/batch-limits.js";
import { useCanvasDecode } from "@/composables/useCanvasDecode";

const { decodeForCanvas, decodeErrorMsg } = useCanvasDecode();

const props = defineProps({
  mode: { type: String, default: "palette" }, // palette | gradient | picker
  slug: { type: String, default: "" },
});

/* ---------------- 共享：toast 反馈 ---------------- */
const toast = ref("");
let toastTimer = null;
function flash(msg) {
  toast.value = msg;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (toast.value = ""), 1500);
}
async function copy(text, label) {
  try {
    if (navigator.clipboard) await navigator.clipboard.writeText(text);
  } catch (_) {}
  flash("Copied " + (label || text));
}

/* ---------------- 共享：颜色换算 ---------------- */
function hslToHex(h, s, l) {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * c).toString(16).padStart(2, "0");
  };
  return ("#" + f(0) + f(8) + f(4)).toUpperCase();
}
function rgbToHex(r, g, b) {
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("").toUpperCase();
}
function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  let h, s, l = (mx + mn) / 2;
  if (mx === mn) { h = s = 0; }
  else {
    const d = mx - mn;
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
    h = mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h /= 6;
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

/* ===================================================================== */
/* 1) PALETTE — 随机和谐配色生成器                                        */
/* ===================================================================== */
const palModes = [
  ["random", "Random"],
  ["analogous", "Analogous"],
  ["monochrome", "Monochrome"],
  ["complementary", "Complementary"],
];
const palMode = ref("random");
const colors = ref([]);
const locked = ref([false, false, false, false, false]);
const rnd = (n) => Math.floor(Math.random() * n);

function genPalette(m) {
  const baseH = rnd(360), baseS = 55 + rnd(35), baseL = 45 + rnd(20);
  return [0, 1, 2, 3, 4].map((i) => {
    if (locked.value[i] && colors.value[i]) return colors.value[i];
    let h, s, l;
    if (m === "analogous") { h = (baseH + i * 25) % 360; s = baseS; l = baseL; }
    else if (m === "monochrome") { h = baseH; s = baseS; l = 25 + i * 14; }
    else if (m === "complementary") { h = (baseH + (i % 2) * 180 + rnd(20)) % 360; s = baseS; l = 35 + i * 9; }
    else { h = rnd(360); s = 50 + rnd(40); l = 40 + rnd(30); }
    return hslToHex(h, s, l);
  });
}
function generate() { colors.value = genPalette(palMode.value); }
function setPalMode(m) { palMode.value = m; generate(); }
function toggleLock(i) { const a = locked.value.slice(); a[i] = !a[i]; locked.value = a; }
function copyAll() { copy(colors.value.join(", "), "palette"); }

function onKeydown(e) {
  if (props.mode !== "palette") return;
  if (e.code === "Space" && e.target && e.target.tagName !== "INPUT") {
    e.preventDefault();
    generate();
  }
}

/* ===================================================================== */
/* 2) GRADIENT — CSS 渐变生成器                                           */
/* ===================================================================== */
const grType = ref("linear");
const grAngle = ref(135);
const grStops = ref([
  { c: "#f97316", p: 0 },
  { c: "#8b5cf6", p: 100 },
]);
const grCss = computed(() => {
  const list = grStops.value
    .slice()
    .sort((a, b) => a.p - b.p)
    .map((s) => `${s.c} ${s.p}%`)
    .join(", ");
  return grType.value === "linear"
    ? `linear-gradient(${grAngle.value}deg, ${list})`
    : `radial-gradient(circle, ${list})`;
});
const grCode = computed(() => "background: " + grCss.value + ";");
function addStop() { grStops.value = grStops.value.concat([{ c: "#22d3ee", p: 50 }]); }
function delStop(i) { if (grStops.value.length > 2) grStops.value = grStops.value.filter((_, j) => j !== i); }
function copyCss() { copy(grCode.value, "CSS"); }

/* ===================================================================== */
/* 3) PICKER — 图片取色 + 自动提取调色板                                  */
/* ===================================================================== */
const stage = ref("drop"); // drop | work
const dragging = ref(false);
const fileInput = ref(null);
const canvas = ref(null);
const fileName = ref("");
const fileDim = ref("");
const thumbUrl = ref("");
const picked = ref({ r: 167, g: 139, b: 250 });
const extracted = ref([]);
let natW = 0, natH = 0, lastUrl = "";

const pickedHex = computed(() => rgbToHex(picked.value.r, picked.value.g, picked.value.b));
const pickedRgbText = computed(() => `${picked.value.r}, ${picked.value.g}, ${picked.value.b}`);
const pickedHslText = computed(() => {
  const [h, s, l] = rgbToHsl(picked.value.r, picked.value.g, picked.value.b);
  return `${h}, ${s}%, ${l}%`;
});
const pickedBg = computed(() => `rgb(${picked.value.r}, ${picked.value.g}, ${picked.value.b})`);

function pickFile() { fileInput.value && fileInput.value.click(); }
function onInput(e) { const f = e.target.files && e.target.files[0]; if (f) loadFile(f); }
function onDrop(e) { e.preventDefault(); dragging.value = false; const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) loadFile(f); }
function revoke() { if (lastUrl) { URL.revokeObjectURL(lastUrl); lastUrl = ""; } }

async function loadFile(file) {
  if (!file || !looksLikeEditableImage(file)) { alert("Please choose an image file."); return; }
  const _m = fileTooLargeMsg(file, "image");
  if (_m) { alert(_m); return; }
  // 浏览器解不了的源(heic/tiff/tga/dds/psd…)先经后端转 png,再进 canvas(用户无感)。
  // 命名/文件名仍取自原始 file,只有 canvas 源 blob 换成 _f。
  let _f;
  try { _f = await decodeForCanvas(file); }
  catch (e) { alert(decodeErrorMsg(file)); return; }
  fileName.value = file.name;
  revoke();
  const url = URL.createObjectURL(_f); lastUrl = url;
  const im = new Image();
  im.onload = async () => {
    natW = im.naturalWidth; natH = im.naturalHeight;
    thumbUrl.value = url;
    fileDim.value = natW + " × " + natH + " px";
    stage.value = "work";
    await nextTick();
    const c = canvas.value; if (!c) return;
    c.width = natW; c.height = natH;
    c.getContext("2d", { willReadFrequently: true }).drawImage(im, 0, 0);
    extractPalette();
    pickAt(0.5, 0.5); // 默认取中心点
  };
  im.onerror = () => { alert("Could not load that image."); };
  im.src = url;
}

function setColor(r, g, b) { picked.value = { r, g, b }; }
function pickAt(fx, fy) {
  const c = canvas.value; if (!c || !natW || !natH) return;
  const x = Math.min(natW - 1, Math.max(0, Math.floor(fx * natW)));
  const y = Math.min(natH - 1, Math.max(0, Math.floor(fy * natH)));
  const d = c.getContext("2d", { willReadFrequently: true }).getImageData(x, y, 1, 1).data;
  setColor(d[0], d[1], d[2]);
}
function onCanvasClick(e) {
  const c = canvas.value; if (!c) return;
  const r = c.getBoundingClientRect();
  pickAt((e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
}

function extractPalette() {
  const c = canvas.value; if (!c) return;
  const sw = 60, sh = Math.max(1, Math.round((60 * natH) / natW));
  const tc = document.createElement("canvas"); tc.width = sw; tc.height = sh;
  const tx = tc.getContext("2d", { willReadFrequently: true });
  tx.drawImage(c, 0, 0, sw, sh);
  const data = tx.getImageData(0, 0, sw, sh).data;
  const buckets = {};
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 128) continue;
    const r = (data[i] >> 5) << 5, g = (data[i + 1] >> 5) << 5, b = (data[i + 2] >> 5) << 5;
    const k = r + "," + g + "," + b;
    buckets[k] = buckets[k] || { c: 0, r: 0, g: 0, b: 0 };
    buckets[k].c++; buckets[k].r += data[i]; buckets[k].g += data[i + 1]; buckets[k].b += data[i + 2];
  }
  extracted.value = Object.values(buckets)
    .sort((a, b) => b.c - a.c)
    .slice(0, 6)
    .map((o) => {
      const r = Math.round(o.r / o.c), g = Math.round(o.g / o.c), b = Math.round(o.b / o.c);
      return { r, g, b, hex: rgbToHex(r, g, b) };
    });
}
function pickFromPalette(p) { setColor(p.r, p.g, p.b); copy(p.hex, p.hex); }
function resetPicker() {
  stage.value = "drop";
  revoke();
  extracted.value = [];
  thumbUrl.value = "";
  natW = 0; natH = 0;
  if (fileInput.value) fileInput.value.value = "";
}

/* ---------------- lifecycle ---------------- */
onMounted(() => {
  if (props.mode === "palette") generate();
  window.addEventListener("keydown", onKeydown);
});
onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeydown);
  if (toastTimer) clearTimeout(toastTimer);
  revoke();
});
watch(() => props.mode, (m) => { if (m === "palette" && !colors.value.length) generate(); });
</script>

<template>
  <div class="ct">
    <!-- ============================ PALETTE ============================ -->
    <template v-if="mode === 'palette'">
      <div class="ct-stage pad">
        <div class="pal-row">
          <div
            v-for="(c, i) in colors"
            :key="i"
            class="pal-swatch"
            :class="{ locked: locked[i] }"
            @click="copy(c, c)"
          >
            <div class="pal-color" :style="{ background: c }">
              <span class="pal-lock" @click.stop="toggleLock(i)">
                <svg v-if="locked[i]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V7a4 4 0 014-4 4 4 0 013.5 2" /></svg>
              </span>
            </div>
            <div class="pal-meta">
              <div class="pal-hex">{{ c }}</div>
              <div class="pal-copy">click to copy</div>
            </div>
          </div>
        </div>
        <div class="pal-bar">
          <div class="pal-modes">
            <button
              v-for="m in palModes"
              :key="m[0]"
              type="button"
              :class="{ active: palMode === m[0] }"
              @click="setPalMode(m[0])"
            >{{ m[1] }}</button>
          </div>
          <div class="pal-actions">
            <button class="btn btn-accent" type="button" @click="generate">Generate</button>
            <button class="btn btn-outline" type="button" @click="copyAll">Copy all</button>
          </div>
        </div>
        <div class="pal-hint">
          Press <kbd>Space</kbd> to generate · click a swatch to copy · click the lock to keep a color
        </div>
      </div>
    </template>

    <!-- ============================ GRADIENT =========================== -->
    <template v-else-if="mode === 'gradient'">
      <div class="ct-stage gr-stage">
        <div class="gr-preview" :style="{ background: grCss }"></div>
        <div class="gr-panel">
          <div class="gr-field">
            <label>Type</label>
            <div class="gr-type">
              <button type="button" :class="{ active: grType === 'linear' }" @click="grType = 'linear'">Linear</button>
              <button type="button" :class="{ active: grType === 'radial' }" @click="grType = 'radial'">Radial</button>
            </div>
          </div>
          <div class="gr-field" :style="{ visibility: grType === 'linear' ? 'visible' : 'hidden' }">
            <label>Angle</label>
            <div class="gr-angle">
              <input type="range" min="0" max="360" v-model.number="grAngle" />
              <span class="v">{{ grAngle }}°</span>
            </div>
          </div>
          <div class="gr-field">
            <label>Color stops</label>
            <div class="gr-stops">
              <div v-for="(s, i) in grStops" :key="i" class="gr-stop">
                <input type="color" v-model="s.c" />
                <input type="range" min="0" max="100" v-model.number="s.p" />
                <span v-if="grStops.length > 2" class="del" @click="delStop(i)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg>
                </span>
              </div>
            </div>
            <button class="addstop" type="button" @click="addStop">+ Add stop</button>
          </div>
          <div class="gr-field">
            <label>CSS</label>
            <div class="gr-code">{{ grCode }}</div>
          </div>
          <button class="btn btn-accent" type="button" @click="copyCss">Copy CSS</button>
        </div>
      </div>
    </template>

    <!-- ============================= PICKER ============================ -->
    <template v-else>
      <input ref="fileInput" type="file" accept="image/*" hidden @change="onInput" />

      <!-- 拖放区 -->
      <div
        v-show="stage === 'drop'"
        class="dz"
        :class="{ drag: dragging }"
        @click="pickFile"
        @dragenter.prevent="dragging = true"
        @dragover.prevent="dragging = true"
        @dragleave.prevent="dragging = false"
        @drop="onDrop"
      >
        <div class="dz-ico">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M19 11l-8 8-4 1 1-4 8-8z" /><path d="M14 6l4 4" /></svg>
        </div>
        <h3>Drop your image here</h3>
        <div class="hint">or click to browse — PNG, JPG, WEBP, GIF</div>
        <div class="formats"><span>PNG</span><span>JPG</span><span>WEBP</span><span>GIF</span></div>
        <div class="dz-or">Processed right in your browser — never stored</div>
        <button class="btn btn-cp" type="button" @click.stop="pickFile">Choose Image</button>
      </div>

      <!-- 工作区 -->
      <div v-if="stage === 'work'" class="ws">
        <div class="ws-grid">
          <div class="cp-canvas-wrap">
            <span class="cp-hint">Click the image to pick a color</span>
            <canvas ref="canvas" class="cp-canvas" @click="onCanvasClick"></canvas>
          </div>
          <div class="ws-panel">
            <div class="ws-file">
              <img class="thumb" :src="thumbUrl" alt="" />
              <div class="meta">
                <div class="fn">{{ fileName }}</div>
                <div class="fsz">{{ fileDim }}</div>
              </div>
              <button class="rm" type="button" title="Remove" @click="resetPicker">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg>
              </button>
            </div>

            <div class="ctl">
              <label>Picked color</label>
              <div class="swatch-big" :style="{ background: pickedBg }"></div>
            </div>
            <div class="ctl" style="gap: 8px">
              <div class="val-row">
                <span class="k">HEX</span>
                <span class="v">{{ pickedHex }}</span>
                <button class="cp-copy" type="button" @click="copy(pickedHex, pickedHex)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 012-2h10" /></svg></button>
              </div>
              <div class="val-row">
                <span class="k">RGB</span>
                <span class="v">{{ pickedRgbText }}</span>
                <button class="cp-copy" type="button" @click="copy(pickedRgbText, 'RGB')"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 012-2h10" /></svg></button>
              </div>
              <div class="val-row">
                <span class="k">HSL</span>
                <span class="v">{{ pickedHslText }}</span>
                <button class="cp-copy" type="button" @click="copy(pickedHslText, 'HSL')"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 012-2h10" /></svg></button>
              </div>
            </div>

            <div class="ctl pal-wrap">
              <label>Extracted palette <span class="sub">· tap to copy</span></label>
              <div class="palette">
                <button
                  v-for="(p, i) in extracted"
                  :key="i"
                  type="button"
                  class="pal"
                  :style="{ background: p.hex }"
                  :title="p.hex"
                  @click="pickFromPalette(p)"
                ><span class="hx">{{ p.hex }}</span></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ============================== TRUST ============================ -->
    <div class="trust">
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>{{ mode === 'picker' ? 'Private — on-device' : 'Runs in your browser' }}</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>{{ mode === 'gradient' ? 'Live preview & code' : mode === 'picker' ? 'Instant HEX / RGB / HSL' : 'Instant & unlimited' }}</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></svg>Free forever</span>
    </div>

    <!-- toast -->
    <transition name="ct-toast">
      <div v-if="toast" class="ct-toast-el">{{ toast }}</div>
    </transition>
  </div>
</template>

<style scoped>
.ct { width: 100%; }
.ct-stage { border: 1px solid var(--line); border-radius: 22px; background: var(--surface); overflow: hidden; }
.ct-stage.pad { padding: 26px; }

/* accent button (orange brand used by palette/gradient) */
.btn-accent { background: linear-gradient(135deg, #ea580c, #f97316); color: #fff; border: none; box-shadow: 0 8px 24px -8px rgba(249, 115, 22, 0.6); }
.btn-accent:hover { transform: translateY(-1px); }

/* ----------------------------- PALETTE ----------------------------- */
.pal-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 14px; margin-bottom: 22px; }
.pal-swatch { border-radius: 14px; overflow: hidden; border: 1px solid var(--line); cursor: pointer; transition: 0.18s; position: relative; }
.pal-swatch:hover { transform: translateY(-3px); }
.pal-color { height: 150px; position: relative; }
.pal-lock { position: absolute; top: 8px; right: 8px; width: 26px; height: 26px; border-radius: 7px; background: rgba(0, 0, 0, 0.4); backdrop-filter: blur(4px); display: grid; place-items: center; color: #fff; opacity: 0.55; transition: 0.15s; cursor: pointer; }
.pal-swatch.locked .pal-lock { opacity: 1; background: rgba(0, 0, 0, 0.65); }
.pal-lock svg { width: 14px; height: 14px; }
.pal-meta { padding: 11px 6px; background: var(--surface-2); text-align: center; }
.pal-hex { font-family: var(--font-h); font-weight: 600; font-size: 14px; letter-spacing: 0.01em; white-space: nowrap; }
.pal-copy { font-size: 11px; color: var(--tx-faint); font-weight: 600; margin-top: 2px; }
.pal-bar { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; }
.pal-modes { display: flex; gap: 6px; flex-wrap: wrap; }
.pal-modes button { padding: 9px 15px; border-radius: 10px; border: 1px solid var(--line); background: var(--surface-2); color: var(--tx-dim); font-family: var(--font-b); font-weight: 700; font-size: 13px; cursor: pointer; transition: 0.15s; }
.pal-modes button.active { background: color-mix(in oklab, #f97316 16%, transparent); border-color: #f97316; color: #fdba74; }
.pal-actions { display: flex; gap: 10px; }
.pal-hint { text-align: center; color: var(--tx-faint); font-size: 13px; font-weight: 600; margin-top: 16px; }
.pal-hint kbd { font-family: var(--font-h); background: var(--surface-2); border: 1px solid var(--line); border-radius: 6px; padding: 2px 7px; font-size: 12px; color: var(--tx); }

/* ----------------------------- GRADIENT ---------------------------- */
.gr-stage { display: grid; grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr); }
.gr-preview { min-height: 340px; }
.gr-panel { padding: 28px; border-left: 1px solid var(--line); display: flex; flex-direction: column; gap: 18px; }
.gr-field label { font-size: 13px; font-weight: 700; color: var(--tx-dim); display: block; margin-bottom: 8px; }
.gr-type { display: flex; gap: 6px; }
.gr-type button { flex: 1; padding: 10px; border-radius: 10px; border: 1px solid var(--line); background: var(--surface-2); color: var(--tx-dim); font-family: var(--font-b); font-weight: 700; font-size: 13px; cursor: pointer; transition: 0.15s; }
.gr-type button.active { background: color-mix(in oklab, #f97316 16%, transparent); border-color: #f97316; color: #fdba74; }
.gr-stops { display: flex; flex-direction: column; gap: 9px; }
.gr-stop { display: flex; align-items: center; gap: 10px; }
.gr-stop input[type="color"] { width: 40px; height: 40px; border: 1px solid var(--line); border-radius: 9px; background: none; cursor: pointer; padding: 3px; flex: none; }
.gr-stop input[type="range"] { flex: 1; }
.gr-stop .del { color: var(--tx-faint); cursor: pointer; padding: 5px; flex: none; }
.gr-stop .del:hover { color: #fb7185; }
.gr-stop .del svg { width: 16px; height: 16px; }
.gr-angle { display: flex; align-items: center; gap: 12px; }
.gr-angle input[type="range"] { flex: 1; }
.gr-angle .v { font-family: var(--font-h); font-weight: 700; color: #fdba74; width: 48px; text-align: right; }
.gr-code { background: #06070b; border: 1px solid var(--line); border-radius: 11px; padding: 13px 14px; font-family: ui-monospace, Menlo, monospace; font-size: 12.5px; color: #cdd3df; word-break: break-all; line-height: 1.5; }
.addstop { align-self: flex-start; background: var(--surface-2); border: 1px solid var(--line); border-radius: 9px; padding: 8px 13px; color: var(--tx-dim); font-weight: 700; font-size: 13px; cursor: pointer; transition: 0.15s; margin-top: 9px; }
.addstop:hover { color: var(--tx); border-color: #f97316; }

/* ------------------------------ PICKER ----------------------------- */
.dz { position: relative; border: 2px dashed var(--line-strong); border-radius: 22px; background: var(--surface); padding: 56px 24px; text-align: center; cursor: pointer; transition: 0.2s; }
.dz.drag, .dz:hover { border-color: #a78bfa; background: linear-gradient(180deg, color-mix(in oklab, #a78bfa 8%, var(--surface)), var(--bg-2)); }
.dz-ico { width: 74px; height: 74px; border-radius: 20px; margin: 0 auto 22px; display: grid; place-items: center; background: color-mix(in oklab, #a78bfa 16%, transparent); color: #a78bfa; }
.dz-ico svg { width: 38px; height: 38px; }
.dz h3 { font-size: 23px; margin-bottom: 9px; }
.dz .hint { color: var(--tx-dim); font-size: 15px; }
.dz .formats { margin-top: 20px; display: flex; flex-wrap: wrap; gap: 7px; justify-content: center; }
.dz .formats span { font-size: 12px; font-weight: 700; letter-spacing: 0.03em; color: var(--tx-faint); border: 1px solid var(--line); border-radius: 7px; padding: 4px 9px; }
.dz-or { margin-top: 22px; color: var(--tx-faint); font-size: 13px; font-weight: 600; margin-bottom: 18px; }
.btn-cp { background: linear-gradient(135deg, #7c3aed, #a78bfa); color: #fff; border: none; box-shadow: 0 8px 24px -8px rgba(167, 139, 250, 0.6); }
.btn-cp:hover { transform: translateY(-1px); box-shadow: 0 12px 30px -8px rgba(167, 139, 250, 0.8); }

.ws { border: 1px solid var(--line); border-radius: 22px; background: var(--surface); overflow: hidden; }
.ws-grid { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); }
.cp-canvas-wrap { position: relative; background: repeating-conic-gradient(#0c0e13 0% 25%, #0a0b10 0% 50%) 50% / 26px 26px; min-height: 360px; display: grid; place-items: center; padding: 20px; cursor: crosshair; overflow: hidden; }
.cp-canvas { max-height: 380px; max-width: 100%; border-radius: 8px; box-shadow: 0 18px 50px -18px rgba(0, 0, 0, 0.85); display: block; }
.cp-hint { position: absolute; top: 14px; left: 14px; font-size: 12px; font-weight: 700; color: #cdbcff; background: rgba(5, 7, 12, 0.6); backdrop-filter: blur(6px); border: 1px solid color-mix(in oklab, #a78bfa 30%, transparent); padding: 5px 11px; border-radius: 99px; pointer-events: none; z-index: 1; }
.ws-panel { border-left: 1px solid var(--line); padding: 26px; display: flex; flex-direction: column; gap: 20px; }
.ws-file { display: flex; align-items: center; gap: 13px; padding-bottom: 18px; border-bottom: 1px solid var(--line); }
.ws-file .thumb { width: 46px; height: 46px; border-radius: 11px; object-fit: cover; flex: none; background: var(--surface-2); }
.ws-file .meta { min-width: 0; flex: 1; }
.ws-file .fn { font-weight: 700; font-size: 14.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ws-file .fsz { font-size: 12.5px; color: var(--tx-faint); font-weight: 600; margin-top: 2px; }
.ws-file .rm { color: var(--tx-faint); cursor: pointer; flex: none; padding: 6px; border-radius: 8px; transition: 0.15s; background: none; border: none; }
.ws-file .rm:hover { color: #a78bfa; background: rgba(255, 255, 255, 0.05); }
.ctl { display: flex; flex-direction: column; gap: 9px; }
.ctl label { font-size: 13px; font-weight: 700; color: var(--tx-dim); letter-spacing: 0.02em; }
.ctl label .sub { color: var(--tx-faint); font-weight: 500; }
.swatch-big { height: 96px; border-radius: 14px; border: 1px solid var(--line); box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06); }
.val-row { display: flex; align-items: center; gap: 10px; background: var(--surface-2); border: 1px solid var(--line); border-radius: 11px; padding: 11px 13px; }
.val-row .k { font-size: 11px; font-weight: 800; letter-spacing: 0.06em; color: var(--tx-faint); width: 38px; flex: none; }
.val-row .v { font-family: var(--font-h); font-weight: 600; font-size: 14.5px; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.val-row .cp-copy { width: 30px; height: 30px; border-radius: 8px; border: 1px solid var(--line); background: var(--surface); color: var(--tx-dim); display: grid; place-items: center; cursor: pointer; flex: none; transition: 0.15s; }
.val-row .cp-copy:hover { color: #cdbcff; border-color: #a78bfa; }
.val-row .cp-copy svg { width: 15px; height: 15px; }
.pal-wrap { padding-bottom: 18px; }
.palette { display: grid; grid-template-columns: repeat(6, 1fr); gap: 8px; }
.pal { aspect-ratio: 1; border-radius: 9px; border: 1px solid var(--line); cursor: pointer; position: relative; transition: 0.15s; padding: 0; }
.pal:hover { transform: translateY(-2px); box-shadow: 0 6px 16px -6px rgba(0, 0, 0, 0.7); }
.pal .hx { position: absolute; bottom: -17px; left: 0; right: 0; text-align: center; font-size: 9px; font-weight: 700; color: var(--tx-faint); opacity: 0; transition: 0.15s; }
.pal:hover .hx { opacity: 1; }

/* ------------------------------ TRUST ------------------------------ */
.trust { display: flex; gap: 22px; justify-content: center; flex-wrap: wrap; margin-top: 18px; }
.trust .i { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; color: var(--tx-dim); }
.trust .i svg { width: 16px; height: 16px; color: #f97316; }

/* ------------------------------ TOAST ------------------------------ */
.ct-toast-el { position: fixed; bottom: 26px; left: 50%; transform: translateX(-50%); background: var(--surface); border: 1px solid #f97316; color: var(--tx); padding: 12px 18px; border-radius: 11px; font-weight: 600; font-size: 13.5px; box-shadow: 0 18px 50px -12px rgba(0, 0, 0, 0.8); z-index: 120; }
.ct-toast-enter-active, .ct-toast-leave-active { transition: 0.25s; }
.ct-toast-enter-from, .ct-toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(20px); }

@media (max-width: 680px) {
  .pal-row { grid-template-columns: 1fr 1fr; }
  .gr-stage { grid-template-columns: 1fr; }
  .gr-panel { border-left: none; border-top: 1px solid var(--line); }
}
@media (max-width: 760px) {
  .ws-grid { grid-template-columns: 1fr; }
  .ws-panel { border-left: none; border-top: 1px solid var(--line); }
}
</style>
